import { PAYMENT_TYPES } from '@vend/business-components'
import { ApiCustomer, ApiStoreCredit, ApiTax, ApiTaxRate } from '@vend/types'
import uuid from '@vend/vend-uuid'
import omit from 'lodash/omit'
import reduce from 'lodash/reduce'
import VendNumber, { isFinite, vn } from 'vend-number'

import remove from 'lodash/remove'
import {
  SALE_VERSION,
  WALKIN_CUSTOMER_CODE,
} from '../components/sale/sale-model.constant'
import {
  createUnknownLineItem,
  removeUnknownLineItemByLocalId,
} from '../functions/unknownLineItem'
import { RegisterManager } from '../managers/register-manager'
import { getSaleDiscountIsPercentage } from '../managers/wr-session-storage'
import {
  AdjustmentName,
  AdjustmentTargetMethod,
  AdjustmentTargetType,
  AdjustmentType,
  AdjustmentValueType,
  GqlAdjustment,
} from '../queries/sale_create'

import { ApiSaleAdjustment, SaleAdjustment } from '../../types/adjustments'
import {
  isOnAccountLimitDisabled,
  isOnAccountLimitUnlimited,
} from '../functions/customer'
import {
  containsGiftCard,
  isGiftCard,
  lineItemToJSON,
} from '../functions/lineItem'
import {
  getCurrentAndOriginalPayments,
  getInclusivePriceConsiderABP,
  getNonKlarnaOutstandingBalance,
  hasChange,
  isABPAddedButNotPaid,
} from '../functions/payments'
import {
  getCashDiscountingAdjustments,
  getCashDiscountingAdjustmentsForMutation,
  getOutstandingBalance,
  getOutstandingBalanceAfterChange,
  getTipAdjustments,
  getTipAdjustmentsForMutation,
  hasOutstandingBalance,
  isAccountBalancePaymentAdded,
  isFirstTimeSubmitEarly,
  isMarkedAsUnfulfilled,
  isNotSavedUnfulfilled,
  isOnAccountLimitReached,
  isPartialPaidSale,
  isReturn,
  isUnfulfilledPickUp,
  isUnpaidSale,
  paymentsHasTip,
} from '../functions/sale'
import { AbpBalanceType } from '../constants/abp-balance-type'
import { CustomFieldValue } from './custom-fields'
import { FulfilmentDetail, FulfilmentType } from './fulfilment'
import {
  LineItem,
  LineItemJson,
  LineItemTaxComponent,
  UnknownLineItem,
} from './line-item'
import { Payment, PaymentJson, paymentToJSON } from './payment'
import {
  SaleDiscount,
  SaleDiscountJSON,
  saleDiscountToJSON,
} from './sale-discount'
import {
  CONFIRMED_STATUSES,
  SYNCED_SYNC_STATUSES,
  SaleMetadata,
  SaleStatus,
  SaleSyncStatus,
  ServiceFields,
  User,
} from './sale.constants'

export const NON_CASH_FEE = {
  type: 'NON_CASH_FEE',
  name: 'Non-cash Fee',
  target_type: 'LINE_ITEM',
  target_method: 'PROPORTIONED',
  value_type: 'FIXED',
} as const
export const NON_CASH_FEE_MUTATION = {
  type: AdjustmentType.NON_CASH_FEE,
  name: AdjustmentName.NON_CASH_FEE,
  targetType: AdjustmentTargetType.LINE_ITEM,
  targetMethod: AdjustmentTargetMethod.PROPORTIONED,
  valueType: AdjustmentValueType.FIXED,
} as const

export const TIP_ADJUSTMENT = {
  type: 'TIP',
  name: 'Tip',
  target_type: 'SALE',
  target_method: 'SINGLE',
  value_type: 'FIXED',
} as const
export const TIP_ADJUSTMENT_MUTATION = {
  type: AdjustmentType.TIP,
  name: AdjustmentName.TIP,
  targetType: AdjustmentTargetType.SALE,
  targetMethod: AdjustmentTargetMethod.SINGLE,
  valueType: AdjustmentValueType.FIXED,
} as const

export const LSP_CASH_DISCOUNT_ADJUSTMENT = {
  type: AdjustmentType.LSP_CASH_DISCOUNT,
  name: AdjustmentName.LSP_CASH_DISCOUNT,
  target_type: AdjustmentTargetType.SALE,
  target_method: AdjustmentTargetMethod.SINGLE,
  value_type: AdjustmentValueType.FIXED,
} as const
export const LSP_CASH_DISCOUNT_ADJUSTMENT_MUTATION = {
  type: AdjustmentType.LSP_CASH_DISCOUNT,
  name: AdjustmentName.LSP_CASH_DISCOUNT,
  targetType: AdjustmentTargetType.SALE,
  targetMethod: AdjustmentTargetMethod.SINGLE,
  valueType: AdjustmentValueType.FIXED,
} as const

// Temp const for Adjustment type surcharge
export const SURCHARGE_ADJUSTMENT = {
  type: 'SURCHARGE',
  target_type: 'SALE',
  target_method: 'SINGLE',
  value_type: 'FIXED',
} as const

export interface SurchargeTenderProperties {
  amount: number
  rule: string
}
export interface ReceiptNumberDetails {
  prefix: string
  number: number
  suffix: string
}

export interface SalePicklist {
  id: string
  type: FulfilmentType
}
interface SaleBase {
  /**
   * The sale model version
   */
  version: number

  /**
   * Set when the sale is completed by a user on the sale confirmation screen. This has been added to help debug
   * sales incorrectly posted the api.
   */
  completedByUser?: { source: string }

  /**
   * The promo code added to the sale
   */
  promoCode: string

  /**
   * The api response status when try to apply a promo code
   */
  promoCodeStatus: string | null

  /**
   * The promo code promotion id
   */
  promoCodePromotionId: string | null

  potentialPromotionId?: string | null

  /**
   * The unique ID of this Sale locally generated on the client.
   */
  localId: string

  id?: string

  /**
   * The unique ID to indentify same Sale for CreateSaleMutation when applying multi retries.
   */
  mutationID?: string

  /**
   * If this Sale is a return, the ID of the original sale this sale is a return for.
   */
  returnFor: string | null

  /**
   * If the Sale is a return, the payments for the original sale.
   */
  originalSalePayments: Payment[]

  /**
   * If the Sale is a return, the surcharge data for the original sale.
   */
  originalSurcharge: OriginalSurcharge | null

  /**
   * Custom charges that were sent by E-Com
   */
  ecomCustomCharges: EcomCustomCharges | null

  /**
   * If the Sale has ecomCustomCharges.
   */
  hasEcomCustomCharges: boolean

  /**
   * Unknown products added to the sale
   */
  unknownLineItems: UnknownLineItem[]

  /**
   * Determines whether to hide the attach customer tooltip
   * The tooltip is hidden once a user has seen it for this sale, or has attached a customer
   */
  hideAttachCustomerTooltip: boolean

  /**
   * Sale Note.
   */
  note: string

  /**
   * The email address where to send the sale receipt.
   */
  receiptEmailAddress: string

  /**
   * The id of the selected receipt to email
   */
  receiptID: string

  /**
   * The remaining balance for this sale
   */
  paidBalance: number | null

  /**
   * payAccountBalance contains the account balance payment information
   * for the sale.
   */
  payAccountBalance: PayAccountBalance | null

  /**
   * Sale payments.
   */
  payments: Payment[]

  /**
   * The short code used for loyalty.
   */
  shortCode?: string

  saleDate?: string | null

  status?: string

  /**
   * Specifies a selected outlet to create the picklist in
   */
  fulfillmentOutletID: string | null

  /**
   * Identify that the local sale can be completed. Assigned when the sale is transitioned to either a
   * layby or onaccount.
   */
  readyToComplete: boolean

  /**
   * Identify that the sale has been submitted to the API upon initial loading of the sale confirmation screen(early submit).
   * This is used to display the confirmation screen for onaccount/layby sales when they are submitted early.
   */
  isSubmittedSaleEarly?: boolean

  registerId?: string | null

  receiptNumberDetails?: ReceiptNumberDetails | string

  /**
   * Interpreted as either a $ or a % depending on the discountIsPercentage property
   */
  displayDiscount: number | string

  discountIsPercentage: boolean

  /**
   * Confirmed sale discount percentage for returns
   */
  returnDiscountPercentage: number | null

  /**
   * The sale discount information
   */
  discount?: SaleDiscount | null

  /**
   * The change on the sale.
   */
  change?: Payment | null

  /**
   * For cash discounting; ensures that a service fee is not applied to a sale if specifically requested by a user.
   */
  userRemovedSaleServiceFee: boolean

  /**
   * LSPay cash discount percentage configured on the sale, as a whole number
   * (e.g. `4` for 4%). `0` (or absent) means no LSPay discount.
   */
  lspCashDiscountPercentage?: number

  /**
   * The totals (price, tax, etc) for this Sale excluding discount.
   * Merge the values in so that changes in this object don't result in
   * errors for sales pulled from IndexedDB created with previous code.
   */
  totals: Totals

  /**
   * The final totals (price, tax, etc) for this Sale including discount.
   */
  finals: Finals

  syncStatus: SaleSyncStatus

  syncAttempts: number

  displayStatus?: SaleStatus

  /**
   * For unfulfilled sales; is the sale in a pickable state?
   */
  pickable?: boolean

  /**
   * For unfulfilled sales. If it is a registered unfulfilled sale?
   */
  isSavedUnfulfilled?: boolean

  /**
   * For saved services. If it is a registered saved service?
   */
  isSavedService?: boolean
  /**
   * For continued non-tax sales. If it is a continued sale with no tax?
   */
  isSavedNonTax?: boolean

  /**
   * True when the sale was loaded from the backend with PARKED status. Stays true even after
   * the sale transitions to another status (e.g. OnAccount), so sync can route correctly.
   */
  isSavedParked?: boolean

  /**
   * For submit earlier sales, to mark if the sale has added customer on confirmation screen
   */
  hasAddedCustomerOnConfirmation?: boolean
  /**
   * For submit earlier sales, to mark if the sale has updated note on confirmation screen
   */
  hasUpdatedNoteOnConfirmation?: boolean

  customFields: CustomFieldValue[]

  fulfilmentDetails: FulfilmentDetail[]

  attributes: string[]
  /**
   * Unfulfilled sale picklists associated with this sale
   */
  picklists?: SalePicklist[]

  /**
   * Stores the original finals before auto-tax was applied for delivery sales
   */
  preAutoTaxFinals?: Finals | null

  /**
   * Stores the original line item tax info before auto-tax was applied for delivery sales
   */
  preAutoTaxLineItems?: Record<
    string,
    {
      tax: string
      baseTax: string
      taxInfo?: ApiTax
      taxComponents: LineItemTaxComponent[]
    }
  >
}

export interface CustomerBalances {
  // Total balance owed by the customer (on-account + layby combined).
  // Sourced from REST `balance` — used throughout the codebase for banner/payment logic.
  account: number
  // On-account portion of the owed balance. Populated from GQL `onAccountBalance`
  // when `on_account_layby_balance_split` FF is on.
  onAccountBalance?: number
  // Layby portion of the owed balance. Populated from GQL `laybyBalance`
  // when `on_account_layby_balance_split` FF is on.
  laybyBalance?: number
  loyalty: number
  availableOnAccountLimitBalance: number
  onAccountLimit: number
  storeCredit: ApiStoreCredit
}

export interface PayAccountBalance {
  amount: number | string
  paymentId?: string
  paymentTypeId?: string
  balanceType?: AbpBalanceType
  /**
   * Synthetic UUID generated at ABP flow start when no sale is created.
   * Used as the LSPay `registerSaleId` correlation key in place of `sale.localId`.
   */
  abpTransactionId?: string
}

export interface SaleTax {
  total: number
  name: string
  removable: boolean
  rate: ApiTaxRate
}

export interface Totals {
  /**
   * The total price excluding tax and excluding discount.
   */
  exclusivePrice: number | string

  /**
   * The total base price (excluding promotions) excluding tax and excluding discount.
   */
  exclusiveBasePrice: number | string

  /**
   * The total tax excluding discount.
   */
  tax: number | string

  /**
   * The total base tax (excluding promotions) excluding discount.
   */
  baseTax: number | string

  /**
   * The total price including tax and excluding discount.
   */
  inclusivePrice: number | string

  /**
   * The total base price (excluding promotions) including tax and excluding discount.
   */
  inclusiveBasePrice: number | string

  /**
   * For cash discounting; the total non-cash fee for the sale.
   */
  saleServiceFee: number | string

  /**
   * For cash discounting; the total service fee paid.
   */
  saleServiceFeePaid: number | string

  /**
   * For cash discounting; the total cash discount to subtract from the sale (because cash payments have been made).
   */
  cashDiscount: number | string

  /**
   * For cash discounting; the LSP cash discount amount.
   */
  lspCashDiscount: number | string

  /**
   * The tax rates present in the sale.
   *
   * @type Array<Tax>
   */
  rates: ApiTaxRate[]

  /**
   * Dependent on whether register is tax inclusive or tax exclusive as to what this value is. It acts as the
   * maximum discount value.
   */
  maxSaleDiscount: number | string

  /**
   * Store finals before auto-tax was applied (for restoration when reverting auto-tax)
   */
  preAutoTaxFinals?: Finals | null

  /**
   * Store original tax info for line items before auto-tax was applied
   */
  preAutoTaxLineItems?: Record<
    string,
    {
      tax: string
      baseTax: string
      taxInfo?: ApiTax
      taxComponents: LineItemTaxComponent[]
    }
  >
}

export interface Finals {
  /**
   * The total price excluding tax and including discount.
   */
  exclusivePrice: number | string

  /**
   * The total tax including discount.
   */
  tax: number | string

  /**
   * The total tax values for each tax rate used in the sale.
   */
  taxes: Record<string, SaleTax>

  /**
   * The total price including tax and discount.
   */
  inclusivePrice: number | string

  /**
   * The display subtotal. Calculated differently for inclusive vs exclusive retailers.
   */
  subtotal: number | string

  /**
   * The total of tip on the sale
   */
  tip: number | string

  /**
   * Display information about surcharge.
   */
  surchargeDisplayProperties: {
    /**
     * Surcharge amount to display
     */
    surcharge: number | string

    /**
     * Surcharge Rule to display
     */
    surchargeRules: string[]
  }

  /**
   * Display information about tax.
   */
  taxDisplayProperties: {
    /**
     * Display name of the currently applied taxes.
     */
    taxName: string

    /**
     * Should we show the remove all option in the tax breakdown?
     */
    showRemoveAll: boolean

    /**
     * Refers to whether there are multiple taxes for display.
     */
    showTaxBreakdown: boolean
  }

  ecomCustomCharges: EcomCustomCharges | null
}

export interface SaleInput extends Partial<SaleBase> {
  customer?: ApiCustomer | null

  lineItems?: LineItem[]

  user?: User | null

  hydratedFromGraphQL?: boolean

  serviceFields?: ServiceFields

  source?: string

  sourceId?: string

  adjustments?: SaleAdjustment[]

  customerBalances?: CustomerBalances | null
  customFields?: CustomFieldValue[]

  metadata?: SaleMetadata[]

  picklists?: SalePicklist[]
}

// using constant and not an enum because ecom flow uses the source dynamically (eg: "ecw:xxxx" - retailer specific)
export const SaleSource = {
  QUOTE: 'quote',
}

export const DEFAULT_TOTALS: Totals = {
  exclusivePrice: '0',
  exclusiveBasePrice: '0',
  tax: '0',
  baseTax: '0',
  inclusivePrice: '0',
  inclusiveBasePrice: '0',
  saleServiceFee: '0',
  saleServiceFeePaid: '0',
  cashDiscount: '0',
  lspCashDiscount: '0',
  rates: [],
  maxSaleDiscount: '0',
}

/** Merge partial totals with defaults. */
export const mergeTotals = (totals?: Partial<Totals>): Totals => ({
  ...DEFAULT_TOTALS,
  ...totals,
})

export const DEFAULT_FINALS: Finals = {
  exclusivePrice: '0',
  tax: '0',
  taxes: {},
  inclusivePrice: '0',
  subtotal: '0',
  tip: '0',
  surchargeDisplayProperties: {
    surcharge: '0',
    surchargeRules: [],
  },
  taxDisplayProperties: {
    taxName: '',
    showRemoveAll: false,
    showTaxBreakdown: false,
  },
  ecomCustomCharges: {
    total: 0,
    totalIncl: 0,
    totalTax: 0,
    charges: [],
  },
}

/**
 * Represents a sale.
 *
 * @class Sale
 * @constructor
 *
 * @param data {Object}
 *      Object with properties and overrides to set on the new sale
 */
export class Sale implements SaleBase {
  public version = SALE_VERSION

  public completedByUser?: { source: string }

  public promoCode: string

  public promoCodeStatus: string | null

  public promoCodePromotionId: string | null

  public potentialPromotionId?: string | null

  public localId: string

  public id?: string

  public mutationID?: string

  public returnFor: string | null

  public originalSalePayments: Payment[]

  public originalSurcharge: OriginalSurcharge | null

  public lineItems: LineItem[]

  public unknownLineItems: UnknownLineItem[]

  public hideAttachCustomerTooltip: boolean

  public note: string

  public receiptEmailAddress: string

  public receiptID: string

  public payments: Payment[]

  /**
   * The customer associated with this Sale.
   */
  public customer: ApiCustomer | null

  /**
   * The customer's balances
   * This excludes any loyalty/store credit earned/redeemed via this sale and its payments
   */
  public customerBalances: CustomerBalances | null

  public shortCode?: string

  public saleDate?: string | null

  public status?: string

  public fulfillmentOutletID: string | null

  public readyToComplete: boolean

  public isSubmittedSaleEarly: boolean

  public registerId?: string | null

  public user?: User | null

  public receiptNumberDetails?: ReceiptNumberDetails | string

  public displayDiscount: number | string

  public discountIsPercentage: boolean

  public returnDiscountPercentage: number | null

  public discount?: SaleDiscount | null

  public change?: Payment | null

  public userRemovedSaleServiceFee: boolean

  public lspCashDiscountPercentage?: number

  public paidBalance: number | null

  public payAccountBalance: PayAccountBalance | null

  public totals: Totals

  public finals: Finals

  public syncStatus: SaleSyncStatus

  public syncAttempts: number

  public displayStatus?: SaleStatus

  public pickable?: boolean

  public isSavedUnfulfilled?: boolean

  public isSavedService?: boolean

  public isSavedNonTax?: boolean

  public isSavedParked?: boolean

  public hasAddedCustomerOnConfirmation?: boolean

  public hasUpdatedNoteOnConfirmation?: boolean

  public hasUpdatedCustomFieldsOnConfirmation?: boolean

  public hydratedFromGraphQL: boolean

  public serviceFields: ServiceFields

  public source?: string

  public sourceId?: string

  // adjustments contains all adjustments from a saved sale and any adjustments made when the sale is active
  public adjustments: SaleAdjustment[]

  public ecomCustomCharges: EcomCustomCharges | null

  public hasEcomCustomCharges: boolean

  public customFields: CustomFieldValue[]

  public fulfilmentDetails: FulfilmentDetail[]

  public attributes: string[]

  public metadata?: SaleMetadata[]

  public picklists?: SalePicklist[]

  public preAutoTaxFinals?: Finals | null

  public preAutoTaxLineItems?: Record<
    string,
    {
      tax: string
      baseTax: string
      taxInfo?: ApiTax
      taxComponents: LineItemTaxComponent[]
    }
  >

  constructor(data?: SaleInput) {
    data = data || {}

    this.completedByUser = data.completedByUser

    this.promoCode = data.promoCode || ''

    this.promoCodeStatus = data.promoCodeStatus || null

    this.promoCodePromotionId = data.promoCodePromotionId || null

    this.potentialPromotionId = data.potentialPromotionId || null

    this.localId = data.localId || uuid()

    this.id = data.id

    this.mutationID = data.mutationID

    this.returnFor = data.returnFor || null

    this.originalSalePayments = data.originalSalePayments || []

    this.originalSurcharge = data.originalSurcharge || null

    this.lineItems = data.lineItems || []

    this.unknownLineItems = data.unknownLineItems || []

    this.hideAttachCustomerTooltip = data.hideAttachCustomerTooltip || false

    this.note = data.note || ''

    this.receiptEmailAddress = data.receiptEmailAddress || ''

    this.receiptID = data.receiptID || ''

    this.paidBalance = data.paidBalance || null

    this.payAccountBalance = data.payAccountBalance || null

    this.payments = data.payments || []

    this.customer = data.customer || null

    this.customerBalances = data.customerBalances || null

    this.shortCode = data.shortCode

    this.saleDate = data.saleDate

    this.status = data.status

    this.fulfillmentOutletID = data.fulfillmentOutletID || null

    this.readyToComplete = data.readyToComplete || false

    this.isSubmittedSaleEarly = data.isSubmittedSaleEarly || false

    this.registerId = data.registerId

    this.user = data.user

    this.receiptNumberDetails = data.receiptNumberDetails

    this.displayDiscount = data.displayDiscount || '0'
    this.discountIsPercentage =
      typeof data.discountIsPercentage === 'boolean'
        ? data.discountIsPercentage
        : getSaleDiscountIsPercentage()

    this.returnDiscountPercentage = data.returnDiscountPercentage || null

    this.discount = data.discount || null

    this.change = data.change

    this.userRemovedSaleServiceFee = data.userRemovedSaleServiceFee || false
    this.lspCashDiscountPercentage = data.lspCashDiscountPercentage ?? 0

    this.totals = mergeTotals(data.totals)

    this.finals = {
      ...DEFAULT_FINALS,
      ...data.finals,
    }

    this.syncStatus = data.syncStatus || SaleSyncStatus.UNMODIFIED
    this.syncAttempts = data.syncAttempts || 0

    this.displayStatus = data.displayStatus
    this.pickable = data.pickable
    this.isSavedUnfulfilled = data.isSavedUnfulfilled || false
    this.isSavedService = data.isSavedService || false
    this.isSavedNonTax = data.isSavedNonTax || false
    this.isSavedParked = data.isSavedParked || false
    this.hasAddedCustomerOnConfirmation =
      data.hasAddedCustomerOnConfirmation || false
    this.hasUpdatedNoteOnConfirmation =
      data.hasUpdatedNoteOnConfirmation || false
    this.hydratedFromGraphQL = data.hydratedFromGraphQL || false
    this.serviceFields = data.serviceFields || {}
    this.source = data.source
    this.sourceId = data.sourceId
    this.adjustments = data.adjustments ?? []
    this.customFields = data.customFields || []
    this.fulfilmentDetails = data.fulfilmentDetails || []
    this.attributes = data.attributes || []
    this.ecomCustomCharges = data.ecomCustomCharges || null
    this.hasEcomCustomCharges = data.hasEcomCustomCharges || false
    this.picklists = data.picklists
    this.preAutoTaxFinals = data.preAutoTaxFinals ?? null
    this.preAutoTaxLineItems = data.preAutoTaxLineItems ?? {}
  }

  /**
   * Add surcharge
   * @param surchargeAmount - the surcharge amount from the payment
   */
  public addSurcharge(surchargeData: SurchargeTenderProperties) {
    const { type } = SURCHARGE_ADJUSTMENT
    this.adjustments.push({
      type,
      name: surchargeData?.rule,
      total: surchargeData?.amount,
    })
  }

  /**
   * Update the account balance payment with the details of the given payment.
   */
  public updatePayAccountBalanceWithPayment(payment: Payment) {
    if (
      this.isABPAddedButNotPaid() &&
      vn(payment.amount).gte(this.payAccountBalance!.amount)
    ) {
      Object.assign(this.payAccountBalance!, {
        paymentId: payment.localId,
        paymentTypeId: payment.paymentType.id,
      })
    }
  }

  /**
   * Calculates the sale total when cash discounting is enabled; subtracting the calculated cash discount from the
   * inclusive price.
   *
   * @return {Number} The sale inclusive price with the total calculated cash discount subtracted
   */
  public getCashDiscountedSaleTotal() {
    return VendNumber.subtract(
      this.finals.inclusivePrice,
      this.totals.cashDiscount
    )
  }

  /**
   * Calculate the outstanding balance by subtracting every payment from the inclusive total.
   *
   * @return {Number} How much still remains to pay on this sale.
   * @deprecated use getOutstandingBalance in /functions/sale.ts instead
   */
  public getOutstandingBalance() {
    return getOutstandingBalance({
      finals: this.finals,
      payAccountBalance: this.payAccountBalance,
      payments: this.payments,
    })
  }

  /**
   * Calculate the outstanding balance by subtracting every non-Klarna payments from non-Klarna refunds.
   *
   * @return {Number} How much non-Klarna amount still remains to on this sale.
   * @deprecated use getNonKlarnaOutstandingBalance in /functions/payments.ts
   * instead
   */
  public getNonKlarnaOutstandingBalance() {
    return getNonKlarnaOutstandingBalance({
      payments: this.payments,
      originalSalePayments: this.originalSalePayments,
    })
  }

  /**
   * Returns the outstanding balance after change has been applied as a payment.
   *
   * @return {Number} Amount remaining to pay on this sale, after change is
   * given.
   * @deprecated use getOutstandingBalanceAfterChange in /functions/sale.ts
   * instead
   */
  public getOutstandingBalanceAfterChange() {
    return getOutstandingBalanceAfterChange({
      change: this.change,
      finals: this.finals,
      payAccountBalance: this.payAccountBalance,
      payments: this.payments,
    })
  }

  /**
   * Determine if the sale total is 0
   *
   * @return {Boolean} Returns true if the sale total is 0, false otherwise
   */
  public isSaleTotalZero() {
    return Boolean(vn(this.finals.inclusivePrice).eq(0))
  }

  /**
   * Determine if the sale total is > 0
   *
   * @return {Boolean} Returns true if the sale total is > 0, false otherwise
   */
  public isSaleTotalGreaterThanZero() {
    return Boolean(vn(this.finals.inclusivePrice).gt(0))
  }

  /**
   * Determine if the sale total is less than 0
   *
   * @return {Boolean} Returns true if the sale total is < 0, false otherwise
   */
  public isSaleTotalLessThanZero() {
    return Boolean(vn(this.finals.inclusivePrice).lt(0))
  }

  /**
   * Determine if the sale has an outstanding balance
   *
   * @return {Boolean} Returns true if the sale has an outstanding balance,
   * false otherwise
   * @deprecated use hasOutstandingBalance in /functions/sale.ts instead
   */
  public hasOutstandingBalance() {
    return hasOutstandingBalance({
      finals: this.finals,
      payments: this.payments,
      payAccountBalance: this.payAccountBalance,
      change: this.change,
    })
  }

  /**
   * Calculate the total amount of payments.
   *
   * @return {Number} How much has been paid.
   * @deprecated use getPaymentsTotal in /functions/sale.ts instead
   */
  public getPaymentsTotal() {
    return this.payments.reduce((total, payment) => {
      const { amount } = payment
      return VendNumber.add(total, amount)
    }, 0)
  }

  /**
   * Find the total number of items in the current sale, as some line items have more than one quantity.
   *
   * @return The total number of items for the current sale.
   * @deprecated use getBasketSize in /functions/lineItem.ts instead
   */
  public getBasketSize() {
    let basketSize: number | string = reduce(
      this.lineItems,
      (total, lineItem) =>
        VendNumber.add(
          // TODO: Unsure how lineItem.quantity is NaN but it does happen and this is throwing errors to Raygun when
          // it does. Rather ignore that line item's quantity in this situation.
          isFinite(lineItem.quantity) ? lineItem.quantity : 0,
          total
        ),
      0
    )
    basketSize = VendNumber.round(basketSize, 3)
    return vn(basketSize).toNumber()
  }

  /**
   * Determine if the customer / line items / note are empty
   *
   * @return True if the sell form is empty, false otherwise.
   */
  public isEmptySale() {
    return Boolean(
      !this.hasValidCustomer() && !this.note && this.lineItems.length === 0
    )
  }

  /**
   * Determine if the parked sale is empty (has no customer/line items/note)
   *
   * @return True if current sale is empty parked sale and not a return, false otherwise.
   */
  public isEmptyParkedSale() {
    return Boolean(
      this.isEmptySale() &&
        this.status === SaleStatus.PARKED &&
        !this.hasPayments() &&
        !isReturn(this)
    )
  }

  /**
   * Return true if any payments have been made against the sale.
   *
   * @return True if the sale has any payments.
   */
  public hasPayments() {
    return this.payments.length > 0
  }

  /**
   * Determine if the sale has line items.
   *
   * @return True if there are line items, false otherwise.
   */
  public hasLineItems() {
    return Boolean(this.lineItems.length)
  }

  /**
   * Used to check if the sale has any customer related payemnts
   * This will include store credits and loyalty
   *
   * @return {Boolean} Returns true if customer related payments exist, false otherwise
   */
  public hasCustomerRelatedPayments() {
    return this.payments.some(payment => {
      const typeId = payment.paymentType.type_id
      return (
        typeId === PAYMENT_TYPES.STORE_CREDIT ||
        typeId === PAYMENT_TYPES.LOYALTY
      )
    })
  }

  /**
   * Calculate how much change needs to be returned, which happens when payments exceed inclusive total. This is a
   * display value so it is absolute.
   *
   * @returns {Number} The absolute change to return when completing this sale
   * @deprecated use getChangeAmountForDisplay in /functions/sale.ts instead
   */
  public getChangeAmountForDisplay() {
    return this.change ? vn(this.change.amount).abs().toNumber() : 0
  }

  /**
   * Determines if the current sale has any change to be returned to the customer
   * @deprecated use hasChange in /function/payments.ts instead
   * @return true if this sale has change to be returned, false otherwise
   */
  public hasChange(): this is this & {
    change: Payment
  } {
    return hasChange(this.change)
  }

  /**
   * Whether this sale has a non-zero discount.
   *
   * @return {Boolean} true if this sale has a discount, false otherwise
   * @deprecated Use hasDiscount in /functions/sale.ts instead
   */
  public hasDiscount(): this is this & {
    discount: SaleDiscount
  } {
    return Boolean(this.discount && parseFloat(this.discount.price as string))
  }

  /**
   * Generate the formatted tip adjustments for the sale.
   *
   * @return {Array} adjustments array
   * @deprecated Use getTipAdjustments in /functions/sale.ts instead
   */
  public getTipAdjustments() {
    return getTipAdjustments(this)
  }

  /**
   * Generate the formatted tip adjustments for the createSaleMutaion input.
   *
   * @return {Array} GqlAdjustment array for tip
   * @deprecated Use getTipAdjustmentsForMutation in /functions/sale.ts instead
   */
  public getTipAdjustmentsForMutation(): GqlAdjustment[] {
    return getTipAdjustmentsForMutation(this)
  }

  /**
   * Generate the formatted cash discounting adjustments for the sale.
   *
   * @return {Array} adjustments array
   * @deprecated use getCashDiscountingAdjustments in /functions/sale.ts instead
   */
  public getCashDiscountingAdjustments(registerManager: RegisterManager) {
    return getCashDiscountingAdjustments(
      this,
      this.lineItems,
      registerManager.getCashDiscountingRate()
    )
  }

  /**
   * Generate the formatted cash discounting adjustments for createSaleMutation input.
   *
   * @return {Array} GqlAdjustment array for lineItem discount
   * @deprecated use getCashDiscountingAdjustmentsForMutation in /functions/sale.ts instead
   */
  public getCashDiscountingAdjustmentsForMutation(
    registerManager: RegisterManager
  ): GqlAdjustment[] {
    return getCashDiscountingAdjustmentsForMutation(
      this,
      this.lineItems,
      registerManager.getCashDiscountingRate()
    )
  }

  /**
   * If there is any surcharge needs to be refunded
   * @deprecated Use hasSurchargeRefundable in /functions/sale.ts instead
   */
  public hasSurchargeRefundable() {
    return vn(this.finals.surchargeDisplayProperties.surcharge).lt(0)
  }

  /**
   * Determines if the sale has any tips associated with currently made payments (current payments made with tip on the sell screen)
   * @returns true if the sale has any tips in its currently made payments
   * @deprecated Use paymentsHasTip in /functions/sale.ts instead
   */
  public paymentsHasTip() {
    return paymentsHasTip(this)
  }

  /**
   * Generate the formatted receipt number details from its parts.
   *
   * @deprecated Use getFormattedReceiptNumberDetails in /functions/sale.ts instead
   * @return The formatted receipt number details
   */
  public getFormattedReceiptNumberDetails() {
    const receiptNumberDetails = this.receiptNumberDetails

    if (typeof receiptNumberDetails === 'string') {
      return receiptNumberDetails
    } else if (receiptNumberDetails) {
      return (
        receiptNumberDetails.prefix +
        receiptNumberDetails.number +
        receiptNumberDetails.suffix
      )
    } else {
      return ''
    }
  }

  /**
   * Returns a JSON representation of this sale suitable for storing in IndexedDB. Referenced models
   * are either replaced with their respective IDs (e.g. customer) or converted to their JSON
   * equivalent (e.g. line item).
   *
   * @return JSON representation of this sale
   */
  public toJSON(): SaleJson {
    const adjustmentToJSON = ({ type, ...rest }: SaleAdjustment) => {
      return {
        adjustment_type: type,
        ...rest,
      }
    }
    return {
      ...omit(this, 'customer', 'user', 'customerBalances'),
      lineItems: this.lineItems.map(lineItemToJSON),
      payments: this.payments.map(paymentToJSON),
      discount: this.discount && saleDiscountToJSON(this.discount),
      change: this.change && paymentToJSON(this.change),
      customer_id: this.customer && this.customer.id,
      user_id: this.user && this.user.id,
      adjustments: this.adjustments.map(adjustmentToJSON),
      ecomCustomCharges: this.ecomCustomCharges,
      hasEcomCustomCharges: this.hasEcomCustomCharges,
      fulfilmentDetails: this.fulfilmentDetails,
      picklists: this.picklists,
    }
  }

  /**
   * Determine whether this sale is "confirmed" or not. A confirmed sale is a Layby or On Account sale that
   * has not been completed (paid off) yet.
   *
   * @return True if this sale is in a confirmed state, false otherwise
   */
  public isConfirmed() {
    return CONFIRMED_STATUSES.includes(this.status as SaleStatus)
  }

  /**
   * Determine if the local sale is in a confirmed state and is ready to be completed
   * @return True is the sale is in a confirmed, ready to complete state, false otherwise
   * @deprecated Use isConfirmedReadyToComplete in /functions/sale.ts instead
   */
  public isConfirmedReadyToComplete() {
    return (
      this.isConfirmed() && (this.readyToComplete || this.isSubmittedSaleEarly)
    )
  }

  /**
   * Determine if the local sale is in a confirmed state and but not ready to complete
   * @return True is the sale is in a confirmed, but not ready to complete
   * @deprecated Use isConfirmedNotReadyToComplete in /functions/sale.ts instead
   */
  public isConfirmedNotReadyToComplete() {
    return (
      this.isConfirmed() && !this.readyToComplete && !this.isSubmittedSaleEarly
    )
  }

  /**
   * Determine whether this sale is an unfulfilled sale
   *
   * @return True if this sale is in a unfulfilled state, false otherwise
   * @deprecated Use isMarkedAsUnfulfilled in /functions/sale.ts instead
   */
  public isMarkedAsUnfulfilled() {
    return isMarkedAsUnfulfilled(this.status as SaleStatus)
  }

  /**
   * Determine whether this sale is a new created unfulfilled sale
   *
   * @deprecated Use isNotSavedUnfulfilled in /functions/sale.ts instead
   */
  public isNotSavedUnfulfilled() {
    return isNotSavedUnfulfilled({
      status: this.status as SaleStatus,
      isSavedUnfulfilled: this.isSavedUnfulfilled,
    })
  }

  /**
   * Responsible for determining whether the sale is PUFs (Partially Paid
   * Unfulfilled Sale) or Partially Paid Service Sale
   *
   * @return {Boolean} True if the sale is PUFs or Partially Paid Service Sale,
   * false otherwise.
   * @deprecated Use isPartialPaidSale in /functions/sale.ts instead
   */
  public isPartialPaidSale(): boolean {
    return isPartialPaidSale({
      status: this.status as SaleStatus,
      payments: this.payments,
      finals: this.finals,
      payAccountBalance: this.payAccountBalance,
    })
  }

  /**
   * Responsible for determining whether the sale is an unpaid unfulfilled  or service sale (no payment at all).
   *
   * @returns {Boolean} True if the sale is an unpaid unfulfilled or service
   * sale, false otherwise.
   * @deprecated Use isUnpaidSale in /functions/sale.ts instead
   */
  public isUnpaidSale(): boolean {
    return isUnpaidSale(this)
  }

  /**
   * Determine whether this sale is an unfulfilled pickup sale
   *
   * @deprecated Use isUnfulfilledPickup in /functions/sale.ts instead
   */
  public isUnfulfilledPickUp() {
    return isUnfulfilledPickUp(this.status)
  }

  /**
   * Determine whether this sale is up to date with the server (either has never been modified on this client
   * or those modifications have been successfully synced).
   *
   * @return True if this sale is up to date with the server
   */
  public isSynced() {
    return SYNCED_SYNC_STATUSES.includes(this.syncStatus)
  }

  /**
   * Return true if the sale has a valid (non-WALKIN) customer suitable for display.
   *
   * @return True if the sale has a valid customer
   * @deprecated Use isValidCustomer in /functions/sale.ts instead
   */
  public hasValidCustomer(): this is this & {
    customer: ApiCustomer
  } {
    const customer = this.customer
    return Boolean(customer && customer.customer_code !== WALKIN_CUSTOMER_CODE)
  }

  /**
   * Check if the sale has added ABP (Account Balance Payment).
   *
   * @return {Boolean} True if ABP has added in the sale, false otherwise
   * @deprecated Use isAccountBalancePaymentAdded in /functions/sale.ts instead
   */
  public isAccountBalancePaymentAdded() {
    return isAccountBalancePaymentAdded(this.payAccountBalance)
  }

  /**
   * Check if the sale has added ABP (Account Balance Payment) but not paid.
   *
   * @return {Boolean} True if ABP has added but not paid, false otherwise
   * @deprecated Use isABPAddedButNotPaid in /functions/payments.ts instead
   */
  public isABPAddedButNotPaid() {
    return isABPAddedButNotPaid(this)
  }

  /**
   * Check if the ABP (Account Balance Payment) has been paid.
   *
   * @return {Boolean} True if ABP has been paid, false otherwise
   */
  public isAccountBalancePaymentPaid() {
    return Boolean(
      this.payAccountBalance?.amount && this.payAccountBalance?.paymentId
    )
  }

  /**
   * Check if the On Account (OA) limit has been reached.
   *
   * @return {Boolean} True if OA limit has been reached, false otherwise
   */
  public isOnAccountLimitReached() {
    return isOnAccountLimitReached({
      customerBalances: this.customerBalances,
      payAccountBalance: this.payAccountBalance,
      payments: this.payments,
      finals: this.finals,
    })
  }

  /**
   * Check if the On Account (OA) limit is unlimited.
   *
   * @return {Boolean} True if OA limit is unlimited, false otherwise
   */
  public isOnAccountLimitUnlimited() {
    return isOnAccountLimitUnlimited(this.customer)
  }

  /**
   * Check if the On Account (OA) limit is disabled.
   *
   * @return {Boolean} True if OA limit is disabled, false otherwise
   */
  public isOnAccountLimitDisabled() {
    return isOnAccountLimitDisabled(this.customer)
  }

  /**
   * Check if current sale is uploaded but not completed
   *
   * @return {Boolean} True if sale is uploaded to be completed, false otherwise
   */
  public isUploadedToBeCompleted() {
    return this.syncStatus === SaleSyncStatus.UPLOADED_TO_COMPLETE
  }

  /**
   * Check if current sale is submit early and it's the first submission
   *
   * @return {Boolean} True if the submit early sale is during the first submission, false otherwise
   * @deprecated Use isFirstTimeSubmitEarly in /functions/payments.ts
   * instead
   */
  public isFirstTimeSubmitEarly() {
    return isFirstTimeSubmitEarly(this)
  }

  /**
   * Get the sale inclusive price considering ABP (Account Balance Payment).
   *
   * @return {Number | String}
   * @deprecated Use getInclusivePriceConsiderABP in /functions/payments.ts
   * instead
   */
  public getInclusivePriceConsiderABP() {
    return getInclusivePriceConsiderABP({
      finals: this.finals,
      payAccountBalance: this.payAccountBalance,
    })
  }

  /**
   * Return true if the sale contains any Gift Card line item
   *
   * @return {Boolean} True if the sale contains any Gift Card line item
   */
  public containsGiftCard() {
    return containsGiftCard(this.lineItems)
  }

  /**
   * Return true if the sale contains a Gift Card line item with the specified number
   *
   * @return True if the sale contains a Gift Card line item with the specified number
   */
  public containsGiftCardByNumber(number: string) {
    return this.lineItems.some(lineItem => {
      return isGiftCard(lineItem.product) && lineItem.giftCardNumber === number
    })
  }

  /**
   * Return array of current and original payment from sale
   *
   * @return payment array
   * @deprecated Use getCurrentAndOriginalPayments in /functions/payments.ts
   */
  public getCurrentAndOriginalPayments() {
    return getCurrentAndOriginalPayments({
      payments: this.payments,
      originalSalePayments: this.originalSalePayments,
    })
  }

  public hasUnknownLineItems() {
    return Boolean(this.unknownLineItems.length)
  }

  public addUnknownLineItem(name: string) {
    this.unknownLineItems = [
      ...this.unknownLineItems,
      createUnknownLineItem(name),
    ]
  }

  public removeUnknownLineItem(item: UnknownLineItem) {
    this.unknownLineItems = removeUnknownLineItemByLocalId(
      this.unknownLineItems,
      item.localId
    )
  }

  // Add, update or remove a customer
  public setCustomer(customer: ApiCustomer | null) {
    this.customer = customer
  }

  /**
   * Return true if the sale is a blind return: at least one line item quantity it negative on a new sale.
   *
   * @return {Boolean} True is the sale is a blind return
   */
  public isBlindReturn(): boolean {
    if (isReturn(this)) {
      return false
    }

    return this.lineItems.some(
      li => isFinite(li.quantity) && vn(li.quantity).lt(0)
    )
  }

  /**
   * Update customer account balances
   */
  public updateCustomerBalances(updates: Partial<CustomerBalances>) {
    if (this.customerBalances) {
      this.customerBalances = {
        ...this.customerBalances,
        ...updates,
      }
    }
  }

  /**
   * Update sale totals
   */
  public setTotals(totals: Totals) {
    this.totals = totals
  }

  /**
   * Update sale finals
   */
  public setFinals(finals: Finals) {
    this.finals = finals
  }

  /**
   * Adds a line item to the sale
   */
  public addLineItem(lineItem: LineItem) {
    this.lineItems.push(lineItem)
  }

  /**
   * Updates a line item in the sale with passed data
   * @param lineItem
   */
  public updateLineItem(lineItem: LineItem) {
    const index = this.lineItems.findIndex(
      li => li.localId === lineItem.localId
    )

    if (index !== -1) {
      this.lineItems[index] = lineItem
    }
  }

  /**
   * Sets the line items for the sale
   * There are instances where one or more properties of all line items in a
   * sale need to be updated. This method will replace all line items in the
   * sale with the updated line items.
   *
   * @param lineItems
   */
  public setLineItems(lineItems: LineItem[]) {
    this.lineItems = [...lineItems]
  }

  /**
   * Removes a line item from the sale
   * @param lineItem
   */
  public removeLineItem(lineItem: LineItem) {
    remove(this.lineItems, lineItem)
  }
}

/**
 * Type of sale stored in IndexedDB. Has no methods, getters, etc.
 */
export interface SaleJson
  extends Omit<SaleBase, 'payments' | 'change' | 'discount'> {
  customer_id: string | null
  user_id?: string | null
  lineItems: LineItemJson[]
  payments: PaymentJson[]
  change?: PaymentJson | null
  adjustments: ApiSaleAdjustment[]
  discount?: SaleDiscountJSON | null
  fulfilmentDetails: FulfilmentDetail[]
  attributes: string[]
  picklists?: SalePicklist[]
  preAutoTaxFinals?: Finals | null
  preAutoTaxLineItems?: Record<
    string,
    {
      tax: string
      baseTax: string
      taxInfo?: ApiTax
      taxComponents: LineItemTaxComponent[]
    }
  >
}

export interface OriginalSurcharge {
  amount: number
  // The total net payment, which excludes the surcharge
  netPaymentAmount: number
}

export interface EcomCustomCharges {
  total: number
  totalIncl: number
  totalTax: number
  charges: EcomCustomCharge[]
}

export interface EcomCustomCharge {
  sourceId: string
  total: number
  totalIncl: number
  totalTax: number
  name: string
}
